import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, ImageOff, Pencil, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtMoney } from "../../../lib/format";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { useApi } from "../../customer/hooks";
import { mediaUrl } from "../../customer/promoTrack";
import { PromotionForm } from "./PromotionForm";
import { useConfirm } from "../../../components/common/ConfirmDialog";

const TONES = { active: "bg-brand/20 text-navy", scheduled: "bg-sky-100 text-sky-700", expired: "bg-slate-100 text-slate-500", draft: "bg-slate-100 text-slate-500", pending: "bg-amber-100 text-amber-800" };

const Row = ({ p, onEdit }) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const st = p.status;
  const act = (path) => api.post(`/promotions/admin/${p.id}/${path}`).then(() => qc.invalidateQueries({ queryKey: ["adminPromotions"] })).catch(() => toast.error(t("common.error")));
  const remove = async () => {
    if (!(await confirm({ title: t("promo.confirmDelete"), danger: true, confirmLabel: t("promo.delete") }))) return;
    try { await api.delete(`/promotions/admin/${p.id}`); qc.invalidateQueries({ queryKey: ["adminPromotions"] }); } catch (e) { toast.error(t("common.error")); }
  };
  return (
    <li className="flex flex-wrap items-center gap-4 p-4" data-testid={`promotion-row-${p.id}`}>
      <div className="h-16 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100">
        {p.image_url ? <img src={mediaUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : <ImageOff className="m-auto mt-5 h-5 w-5 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-navy">{p[`title_${lang}`] || p.title_el}</p>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONES[st]}`} data-testid={`promotion-status-${p.id}`}>{t(`x.offers.status.${st}`)}</span>
        </div>
        <p className="text-xs text-slate-500">{fmtDate(p.start_date)} – {fmtDate(p.end_date)} · {t(`promo.target${p.target.type[0].toUpperCase()}${p.target.type.slice(1)}`)} · {t("promo.items")}: {p.item_codes.length}</p>
      </div>
      <div className="flex items-center gap-4 text-sm tabular-nums text-navy">
        <span className="flex items-center gap-1" data-testid={`promotion-views-${p.id}`}><Eye className="h-4 w-4 text-slate-400" /> {p.views}</span>
        <span className="flex items-center gap-1" data-testid={`promotion-adds-${p.id}`}><ShoppingCart className="h-4 w-4 text-slate-400" /> {p.add_to_cart}</span>
        <span className="text-xs text-slate-500" data-testid={`promotion-popups-${p.id}`}>{t("x.offers.popups")}: {p.popup}</span>
        <span className="text-xs text-slate-500" data-testid={`promotion-ordered-${p.id}`}>{t("x.offers.ordered")}: {p.ordered_qty} · {fmtMoney(p.ordered_value)}</span>
      </div>
      <div className="flex gap-1">
        {st === "pending" && hasPerm(user, "offers:approve") && <button type="button" onClick={() => act("approve")} data-testid={`promotion-approve-${p.id}`} className="h-11 rounded-full bg-brand px-3 text-xs font-semibold text-navy">{t("x.offers.approve")}</button>}
        <button type="button" onClick={() => act("duplicate")} aria-label={t("x.offers.duplicate")} data-testid={`promotion-duplicate-${p.id}`} className="grid h-11 w-11 place-items-center rounded-full hover:bg-slate-100"><Copy className="h-4 w-4" /></button>
        <button type="button" onClick={onEdit} aria-label={t("promo.edit")} data-testid={`promotion-edit-${p.id}`} className="grid h-11 w-11 place-items-center rounded-full hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
        <button type="button" onClick={remove} aria-label={t("promo.delete")} data-testid={`promotion-delete-${p.id}`} className="grid h-11 w-11 place-items-center rounded-full text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
};

export default function PromotionsAdmin() {
  const { t } = useI18n();
  const q = useApi("adminPromotions", "/promotions/admin");
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-6" data-testid="promotions-admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-3xl font-bold text-navy">{t("promo.admin")}</h1><p className="mt-1 text-sm text-slate-500">{t("promo.adminSub")}</p></div>
        <button type="button" onClick={() => setEditing("new")} data-testid="promotion-new-button" className="flex h-11 items-center gap-1.5 rounded-full bg-brand px-5 text-sm font-semibold text-navy"><Plus className="h-4 w-4" /> {t("promo.new")}</button>
      </div>
      {(q.data || []).length === 0 ? <p className="text-sm text-slate-500" data-testid="promotions-empty">{t("promo.none")}</p> : (
        <ul className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm" data-testid="promotions-list">
          {q.data.map((p) => <Row key={p.id} p={p} onEdit={() => setEditing(p)} />)}
        </ul>
      )}
      {editing && <PromotionForm key={editing === "new" ? "new" : editing.id} promo={editing === "new" ? null : editing} open onClose={() => setEditing(null)} />}
    </div>
  );
}
